import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { itemKeys } from "@/lib/query";

interface Item {
  id: string;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  createdAt: string;
}

interface ItemDetail extends Item {
  comments: Comment[];
}

interface ItemsListResponse {
  data: Item[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

export function useItems(filters: { search: string; status: string }, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...itemKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<ItemsListResponse> => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.search) params.set("search", filters.search);
      if (filters.status) params.set("status", filters.status);

      const res = await fetch(`/api/items?${params}`);
      if (!res.ok) throw new Error("Failed to fetch items");
      return res.json();
    },
  });
}

export function useItem(itemId: string) {
  return useQuery({
    queryKey: itemKeys.detail(itemId),
    queryFn: async (): Promise<ItemDetail> => {
      const res = await fetch(`/api/items/${itemId}`);
      if (!res.ok) throw new Error("Failed to fetch item");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useUpdateItem(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: {
      name?: string;
      description?: string | null;
      status?: string;
    }) => {
      const res = await fetch(`/api/items/${itemId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemKeys.all });
      toast.success("Item updated");
    },
    onError: () => {
      toast.error("Failed to update item");
    },
  });
}

export function useAddComment(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/items/${itemId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error("Failed to add comment");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemKeys.detail(itemId) });
      toast.success("Comment added");
    },
    onError: () => {
      toast.error("Failed to add comment");
    },
  });
}

export function useDeleteComment(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      const res = await fetch(`/api/items/${itemId}/comments/${commentId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemKeys.detail(itemId) });
      toast.success("Comment deleted");
    },
    onError: () => {
      toast.error("Failed to delete comment");
    },
  });
}
